import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {ISelectOption} from '../../../shared-models/custom-select/custom-select';
import {ColDef} from 'ag-grid-community';
import {
  SharedCustomSelectComponent,
} from '../shared-custom-select/components/shared-custom-select/shared-custom-select.component';
import {LocalStorageService} from '../../../../features/core/service';
import {LocalStorageKeyType} from '../../../../features/core/models';
import {
  SaveUserSettingsPage,
} from '../../../../shared-modules/shared-module/modules-old/save-user-settings/save-user-settings.page';
import {
  IModalDismissResult,
  IModalRef,
  ModalControllerService,
} from '../../../shared-services/overlay/modal-controller.service';
import {Store} from '@ngrx/store';
import {
  SharedLoadingModalComponent,
} from '../shared-loading/components/shared-loading-modal/shared-loading-modal.component';
import {ensureDepartmentNameFields} from '../../../shared-utils/stock/department-display.utils';

@Component({
  selector: 'app-shared-visible-fields',
  templateUrl: './shared-visible-fields.component.html',
  styleUrls: ['./shared-visible-fields.component.scss'],
  standalone: false,
})
export class SharedVisibleFieldsComponent implements OnInit {
  @ViewChild('visibleFieldsSelect') visibleFieldsSelect: SharedCustomSelectComponent;

  @Input() localStorageOrderKey: LocalStorageKeyType;
  @Input() document?: string;
  @Input() localStorageSizeKey: LocalStorageKeyType;
  @Input() label: string = 'Visible Fields';
  @Input() isFitToGrid?: boolean;
  @Output() readonly emitSelectedFields = new EventEmitter<ColDef[]>();
  @Output() readonly fitToGridRestored = new EventEmitter<boolean>();

  visibleFieldsOptions: Record<string, ISelectOption> = {};
  selectedVisibleFields: string[] = [];
  isMobile: boolean = false;
  protected readonly iconColourEnum = IconColourEnum;
  protected readonly icons = IconsEnum;

  private gridColDefsInternal: ColDef[];
  defaultColDefs: ColDef[];
  private columnOrderFromFirebase: string[];
  private componentReady = false;

  constructor(
    private localStorageService: LocalStorageService,
    private modalController: ModalControllerService,
    private readonly store: Store) {
  }

  get gridColDefs(): ColDef[] {
    return this.gridColDefsInternal;
  }

  get getSelectedVisibleFields(): string[] {
    return this.selectedVisibleFields;
  }

  get getVisibleFieldsOptions(): Record<string, ISelectOption> {
    return this.visibleFieldsOptions;
  }

  get getLocalStorageKeys(): { order: LocalStorageKeyType; size: LocalStorageKeyType } {
    return {
      order: this.localStorageOrderKey,
      size: this.localStorageSizeKey,
    };
  }

  get getDefaultFields(): Record<string, ISelectOption> {
    const options: Record<string, ISelectOption> = {};

    this.defaultColDefs?.forEach((colDef: ColDef) => {
      if (colDef.field && colDef.headerName) {

        const option: ISelectOption = {
          name: colDef.headerName,
          selected: !colDef.hide,
          disabled: colDef.lockVisible === true,
        };

        options[colDef.field] = option;
      }
    });
    return options;
  }

  @Input() set setGridColDefs(colDefs: ColDef[]) {
    this.gridColDefsInternal = colDefs;
    this.tryInitializeVisibleFieldsOptions();
  }

  @Input() set setDefaultGridColDefs(colDefs: ColDef[]) {
    if (!this.defaultColDefs) {
      this.defaultColDefs = colDefs;
    }
  }

  ngOnInit(): void {
    this.componentReady = true;
    this.tryInitializeVisibleFieldsOptions();
    this.isMobile = window.innerWidth < 600;
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 600;
    });
  }

  onVisibleFieldsButtonClick(event: Event): void {
    event?.stopPropagation();
    if (this.visibleFieldsSelect) {
      this.visibleFieldsSelect.toggleDropdown();
    }
  }

  onVisibleFieldsChange(fields: Record<string, ISelectOption>): void {
    if (!this.gridColDefs) return;

    // 1) Apply visibility
    const updatedColDefs: ColDef[] = this.gridColDefs.map((colDef: ColDef) => {
      const f = colDef.field;
      if (f && fields[f]) {
        return {...colDef, hide: !fields[f].selected};
      }
      return colDef;
    });
    // 2) Compute selected fields
    this.visibleFieldsOptions = {...fields};
    this.selectedVisibleFields = Object.keys(fields).filter(k => fields[k].selected);

    // 3) Build the column order to persist
    let orderedColumns: string[] = [];
    if (this.columnOrderFromFirebase?.length) {
      orderedColumns = [...this.columnOrderFromFirebase];
    } else {
      for (const cd of updatedColDefs) {
        if (cd.field && (this.selectedVisibleFields.includes(cd.field) || cd.lockVisible)) orderedColumns.push(cd.field);
      }
    }

    const orderIndex = new Map<string, number>();
    orderedColumns.forEach((f, i) => orderIndex.set(String(f), i));

    const withOrigIdx = updatedColDefs.map((c, i) => ({c, i}));
    withOrigIdx.sort((a, b) => {
      const af = a.c.field ?? '';
      const bf = b.c.field ?? '';
      const ai = orderIndex.has(af) ? (orderIndex.get(af)) : Number.MAX_SAFE_INTEGER;
      const bi = orderIndex.has(bf) ? (orderIndex.get(bf)) : Number.MAX_SAFE_INTEGER;
      if (ai !== bi) return ai - bi;
      return a.i - b.i; // keep original relative order for unmapped or equal
    });

    const reorderedColDefs = withOrigIdx.map(x => x.c);

    // 5) Persist and emit
    this.gridColDefsInternal = reorderedColDefs;
    this.columnOrderFromFirebase = null;
    this.localStorageService.setLocalStorageItem(this.getLocalStorageKeys.order, orderedColumns);
    this.emitSelectedFields.emit(reorderedColDefs);
  }


  async onVisibleFieldSaveButtonClick(fields: Record<string, ISelectOption>): Promise<void> {
    const visibleFieldsToSave = {};
    const colDefData: ColDef[] = [];
    const storageKey = this.getLocalStorageKeys.size;
    const storageKeyOrder = this.getLocalStorageKeys.order;
    const colDefsSize: Record<string, number> = JSON.parse(localStorage.getItem(storageKey));
    const localColDefs = JSON.parse(localStorage.getItem(storageKeyOrder));
    localColDefs?.forEach((column: string): void => {
      const index = this.gridColDefs.findIndex((col: ColDef) => col.field === column);
      if (index === -1) {
        return;
      }

      if (colDefsSize?.[column]) {
        colDefData.push(this.gridColDefs[index]);
        visibleFieldsToSave[column] = colDefsSize[column];
      } else {

        if (!this.gridColDefs[index].hide) {
          colDefData.push(this.gridColDefs[index]);
          visibleFieldsToSave[column] = this.gridColDefs[index].width;
        }
      }
    });
    const modalController = await this.modalController.create({
      component: SaveUserSettingsPage, componentProps: {
        getSaveObj: (() => visibleFieldsToSave),
        currentRoute: this.document,
        what: `Visible Fields`,
        entryPrefix: 'ag_grid',
        entrySuffix: this.document,
        colDefs: colDefData,
        localStorageKey: storageKey,
        isFitToGrid: this.isFitToGrid ?? false,
      },
    });
    await modalController.present();
    const loadResponse: IModalDismissResult<{
      colDefs: string[],
      data: ColDef[],
      isFitToGrid?: boolean,
    }> = await modalController.onDidDismiss();
    if (loadResponse?.data?.data) {
      const loadingModal: IModalRef = await this.presentLayoutLoadingModal();
      try {
        this.applyLoadedVisibleFieldsLayout(loadResponse.data, storageKeyOrder);
        await this.waitForLayoutApply();
      } finally {
        await loadingModal.dismiss().catch(() => undefined);
      }
    }
  }

  /**
   * Applies a saved visible-fields layout from the Save/Load modal dismiss payload.
   */
  private applyLoadedVisibleFieldsLayout(
    loadedLayout: {colDefs: string[]; data: ColDef[]; isFitToGrid?: boolean},
    storageKeyOrder: LocalStorageKeyType,
  ): void {
    const layoutFieldIds: string[] = ensureDepartmentNameFields(loadedLayout.colDefs ?? []);
    this.gridColDefs.forEach((col: ColDef): void => {
      col = {...col};
      col.hide = layoutFieldIds.includes(col.field);
    });
    localStorage.setItem(storageKeyOrder, JSON.stringify(layoutFieldIds));
    Object.keys(loadedLayout.data).forEach((field: string) => {
      const index = this.gridColDefs.findIndex((colDef: ColDef) => colDef.field === field);
      if (index === -1) {
        return;
      }
      this.gridColDefs[index].width = loadedLayout.data[field].width ?? loadedLayout.data[field];
    });
    this.columnOrderFromFirebase = layoutFieldIds;
    const updatedFields: Record<string, ISelectOption> = {};
    this.gridColDefs.forEach((colDef: ColDef) => {
      if (colDef.field && colDef.headerName) {
        updatedFields[colDef.field] = {
          name: colDef.headerName,
          selected: layoutFieldIds.includes(colDef.field),
          disabled: colDef.lockVisible === true,
        };
      }
    });
    this.onVisibleFieldsChange(updatedFields);
    if (typeof loadedLayout.isFitToGrid === 'boolean') {
      this.fitToGridRestored.emit(loadedLayout.isFitToGrid);
    }
  }

  /**
   * Shows the shared Gallix loading modal while a visible-fields layout is applied.
   */
  private async presentLayoutLoadingModal(): Promise<IModalRef> {
    const loadingModal: IModalRef = await this.modalController.create({
      component: SharedLoadingModalComponent,
      componentProps: {
        message: 'Loading visible fields layout…',
      },
      cssClass: ['shared-basic-modal-css-small', 'shared-loading-modal-css'],
      backdropDismiss: false,
    });
    await loadingModal.present();
    return loadingModal;
  }

  /**
   * Yields briefly so Angular and AG Grid can finish applying column changes.
   */
  private async waitForLayoutApply(): Promise<void> {
    await new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, 300);
    });
  }

  /**
   * Initializes visible-field options once the component lifecycle has started and column
   * definitions contain at least one field with a header name.
   */
  private tryInitializeVisibleFieldsOptions(): void {
    if (!this.componentReady || !this.areColDefsReady(this.gridColDefsInternal)) {
      return;
    }
    this.initializeVisibleFieldsOptions();
  }

  private areColDefsReady(colDefs: ColDef[] | undefined): boolean {
    return Array.isArray(colDefs)
      && colDefs.length > 0
      && colDefs.some((colDef: ColDef) => !!colDef.field && !!colDef.headerName);
  }

  private initializeVisibleFieldsOptions(): void {
    if (!this.gridColDefsInternal) {
      return;
    }
    const colsSnapshot = [...this.gridColDefsInternal];
    this.visibleFieldsOptions = {};
    this.selectedVisibleFields = [];

    const optionsArray: { field: string, option: ISelectOption }[] = [];
    const storedOrder = ensureDepartmentNameFields(
      this.localStorageService.getLocalStorageItem<string[]>(this.localStorageOrderKey) ?? [],
    );
    if (storedOrder.length > 0) {
      this.localStorageService.setLocalStorageItem<string[]>(this.localStorageOrderKey, storedOrder);
    }
    const orderIndex = new Map<string, number>();
    if (storedOrder?.length) {
      storedOrder.forEach((field: string, index: number) => orderIndex.set(field, index));
    }

    colsSnapshot.forEach((colDef: ColDef) => {
      if (colDef.field && colDef.headerName) {

        const option: ISelectOption = {
          name: colDef.headerName,
          selected: colDef.hide !== true,
          disabled: colDef.lockVisible === true,
        };

        optionsArray.push({ field: colDef.field, option });
      }
    });

    if (storedOrder?.length) {
      this.selectedVisibleFields = storedOrder.filter((field: string) => {
        const match = optionsArray.find((entry) => entry.field === field);
        return match?.option.selected === true;
      });
      optionsArray.forEach(({field, option}) => {
        if (option.selected && !this.selectedVisibleFields.includes(field)) {
          this.selectedVisibleFields.push(field);
        }
      });
    } else {
      optionsArray.forEach(({field, option}) => {
        if (option.selected) {
          this.selectedVisibleFields.push(field);
        }
      });
    }

    optionsArray.sort((a, b) => {
      if (a.option.disabled !== b.option.disabled) {
        return a.option.disabled ? 1 : -1;
      }
      if (a.option.selected !== b.option.selected) {
        return a.option.selected ? -1 : 1;
      }
      const indexA = orderIndex.has(a.field) ? orderIndex.get(a.field) : Number.MAX_SAFE_INTEGER;
      const indexB = orderIndex.has(b.field) ? orderIndex.get(b.field) : Number.MAX_SAFE_INTEGER;
      if (indexA !== indexB) {
        return indexA - indexB;
      }
      return a.option.name.localeCompare(b.option.name);
    });
    optionsArray.forEach(({field, option}) => {
      this.visibleFieldsOptions[field] = option;
    });
  }


}
